/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import "moment/locale/th";

// FullCalendar components & plugins
import Fullcalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import Chip from "@material-ui/core/Chip";
import Avatar from "@material-ui/core/Avatar";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";

// @material-ui/icons
import AccessTime from "@material-ui/icons/AccessTime";
import FlightTakeoff from "@material-ui/icons/FlightTakeoff";
import Assignment from "@material-ui/icons/Assignment";
import Today from "@material-ui/icons/Today";
import CheckCircle from "@material-ui/icons/CheckCircle";
import Close from "@material-ui/icons/Close";
import Search from "@material-ui/icons/Search";
import Group from "@material-ui/icons/Group";
import SupervisorAccount from "@material-ui/icons/SupervisorAccount";
import Settings from "@material-ui/icons/Settings";

// Core components
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import CardBody from "components/Card/CardBody.js";
import CardFooter from "components/Card/CardFooter.js";
import Button from "components/CustomButtons/Button.js";
import TSSelect2 from "components/Forms/TSSelect2";

const useStyles = makeStyles((theme) => ({
  cardCategoryWhite: {
    color: "rgba(255,255,255,.8)",
    margin: "0",
    fontSize: "14px",
    marginTop: "0",
    marginBottom: "0",
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "500",
    fontFamily: '"Sarabun", sans-serif',
    marginBottom: "3px",
    textDecoration: "none",
  },
  statsCategory: {
    color: "#777",
    margin: "0",
    fontSize: "13px",
    fontWeight: "bold",
  },
  statsTitle: {
    color: "#3C4858",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "bold",
    fontFamily: '"Sarabun", sans-serif',
    fontSize: "26px",
    marginBottom: "3px",
  },
  statsUnit: {
    fontSize: "16px",
    fontWeight: "normal",
    color: "#888",
    marginLeft: "4px",
  },
  tableResponsive: {
    width: "100%",
    overflowX: "auto",
  },
  calendarCard: {
    "& .fc": {
      fontFamily: '"Sarabun", sans-serif',
    },
    "& .fc-toolbar-title": {
      fontSize: "1.25rem !important",
      fontWeight: "bold",
      color: "#333",
    },
    "& .fc-button-primary": {
      backgroundColor: "#1976d2 !important",
      borderColor: "#1976d2 !important",
      textTransform: "capitalize",
      fontWeight: "bold",
      boxShadow: "none !important",
      "&:hover": {
        backgroundColor: "#1565c0 !important",
      },
    },
    "& .fc-button-active": {
      backgroundColor: "#0d47a1 !important",
      borderColor: "#0d47a1 !important",
    },
    "& .fc-event": {
      borderRadius: "4px",
      padding: "2px 5px",
      fontSize: "12px",
      cursor: "pointer",
      border: "none",
      boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
      transition: "transform 0.15s ease",
      "&:hover": {
        transform: "scale(1.02)",
        filter: "brightness(0.95)",
      },
    },
    "& .fc-day-today": {
      backgroundColor: "rgba(25, 118, 210, 0.06) !important",
    },
    "& .fc-col-header-cell": {
      backgroundColor: "#f5f7fa",
      padding: "8px 0",
      fontWeight: "bold",
      color: "#555",
    },
  },
  legendContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
    padding: "12px 16px",
    backgroundColor: "#fafafa",
    borderRadius: "6px",
    marginBottom: "16px",
    alignItems: "center",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12.5px",
    fontWeight: "500",
    color: "#444",
  },
  legendDot: {
    width: "12px",
    height: "12px",
    borderRadius: "3px",
    display: "inline-block",
  },
  tabCustom: {
    fontWeight: "bold",
    fontFamily: '"Sarabun", sans-serif',
    fontSize: "14px",
  },
  statusIconApproved: {
    color: "#2e7d32",
    fontSize: "18px",
    fontWeight: "bold",
  },
  statusIconPending: {
    color: "#f57c00",
    fontSize: "18px",
    fontWeight: "bold",
  },
  statusIconRejected: {
    color: "#d32f2f",
    fontSize: "18px",
    fontWeight: "bold",
  },
}));

const HRAttendance = () => {
  const classes = useStyles();
  const history = useHistory();
  const calendarRef = useRef(null);

  // ดึงปีงบประมาณแบบเดียวกับ HRApprove
  const [optionYear, setOptionYear] = useState([]);
  const [year, setYear] = useState(0);

  const currentMonthNum = moment().month() + 1; // 1..12
  const [selectedMonth, setSelectedMonth] = useState(currentMonthNum); // default เป็นเดือนปัจจุบัน
  const [tabIndex, setTabIndex] = useState(0); // 0: ทั้งหมด, 1: กจ. 1/กจ. 4, 2: การลา
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Filters สำหรับระบบลา (Frontend Filter)
  const [leaveTypeList, setLeaveTypeList] = useState([{ value: 0, label: "ทุกประเภท" }]);
  const [leaveTypeFilter, setLeaveTypeFilter] = useState(0); // 0=ทุกประเภท
  const [hrApproveFilter, setHrApproveFilter] = useState(0); // 0=ทั้งหมด, 1=?, 2=✓, -1=X
  const [drApproveFilter, setDrApproveFilter] = useState(0); // 0=ทั้งหมด, 1=?, 2=✓, -1=X

  // ข้อมูลภาพรวมทั้งหน่วยงาน
  const [attendanceList, setAttendanceList] = useState([]);
  const [leaveList, setLeaveList] = useState([]);

  // ตั้งค่าปีงบประมาณเริ่มต้น และสร้าง optionYear เหมือน HRApprove
  useEffect(() => {
    var startYear = parseInt(moment().format("BBBB"));
    if (isNaN(startYear)) {
      startYear = parseInt(moment().format("YYYY")) + 543;
    }
    if (moment().month() >= 9) {
      startYear = startYear + 1;
    }
    setYear(startYear);

    let tempOptionYear = [];
    for (var i = startYear + 1; i > startYear - 10; i--) {
      tempOptionYear.push({ value: i, label: i });
    }
    setOptionYear(tempOptionYear);

    // ดึงประเภทการลาทั้งหมดเพื่อใช้ใน Dropdown filter
    axios("/api/v1/leave/leavetypeall")
      .then((res) => {
        if (res.data?.data) {
          const types = res.data.data.map((item) => ({
            value: item.leaveTypeKey,
            label: item.name,
          }));
          setLeaveTypeList([{ value: 0, label: "ทุกประเภท" }, ...types]);
        }
      })
      .catch((err) => console.log(err));
  }, []);

  // ตัวเลือกเดือนปฏิทิน (12 เดือน ไม่มีทุกเดือน)
  const monthOptions = [
    { value: 10, label: "ตุลาคม" },
    { value: 11, label: "พฤศจิกายน" },
    { value: 12, label: "ธันวาคม" },
    { value: 1, label: "มกราคม" },
    { value: 2, label: "กุมภาพันธ์" },
    { value: 3, label: "มีนาคม" },
    { value: 4, label: "เมษายน" },
    { value: 5, label: "พฤษภาคม" },
    { value: 6, label: "มิถุนายน" },
    { value: 7, label: "กรกฎาคม" },
    { value: 8, label: "สิงหาคม" },
    { value: 9, label: "กันยายน" },
  ];

  const approveFilterOptions = [
    { label: "ทั้งหมด", value: 0 },
    { label: "?", value: 1 },
    { label: "✓", value: 2 },
    { label: "X", value: -1 },
  ];

  // เมื่อเปลี่ยนปีจาก Dropdown -> ให้ปฏิทินเลื่อนตามปีและเดือนที่เลือก
  const handleYearChange = (newYearVal) => {
    setYear(newYearVal);
    if (calendarRef.current && newYearVal > 0) {
      const targetGy =
        selectedMonth >= 10
          ? newYearVal > 2500
            ? newYearVal - 544
            : newYearVal - 1
          : newYearVal > 2500
          ? newYearVal - 543
          : newYearVal;
      const targetDateStr = `${targetGy}-${selectedMonth.toString().padStart(2, "0")}-01`;
      calendarRef.current.getApi().gotoDate(targetDateStr);
    }
  };

  // เมื่อเปลี่ยนเดือนจาก Dropdown -> ให้ปฏิทินเลื่อนตาม
  const handleMonthChange = (monthVal) => {
    setSelectedMonth(monthVal);
    if (calendarRef.current && year > 0) {
      const targetGy =
        monthVal >= 10
          ? year > 2500
            ? year - 544
            : year - 1
          : year > 2500
          ? year - 543
          : year;
      const targetDateStr = `${targetGy}-${monthVal.toString().padStart(2, "0")}-01`;
      calendarRef.current.getApi().gotoDate(targetDateStr);
    }
  };

  // เมื่อเลื่อนเดือน/ปีบนปฏิทิน FullCalendar -> ให้ Dropdown เดือนและปีเปลี่ยนตามอัตโนมัติ
  const handleDatesSet = (dateInfo) => {
    const viewMoment = moment(dateInfo.view.currentStart);
    const viewMonth = viewMoment.month() + 1; // 1 - 12
    const viewGy = viewMoment.year();
    const viewFiscalYear = viewMonth >= 10 ? viewGy + 544 : viewGy + 543;

    if (viewMonth !== selectedMonth) {
      setSelectedMonth(viewMonth);
    }
    if (year > 0 && viewFiscalYear !== year) {
      setYear(viewFiscalYear);
    }
  };

  // ดึงข้อมูลสำหรับ HR ตรวจสอบภาพรวมทั้งหน่วยงาน (ดึงครั้งเดียวตามปีงบประมาณ)
  const fetchAllData = (targetYear) => {
    const fetchYear = targetYear || year;
    if (!fetchYear || fetchYear <= 0) return;

    setLoading(true);

    // 1. ดึงรายการขอลงเวลา กจ. 1 / กจ. 4 ทั้งหน่วยงาน
    const fetchAttendance = axios
      .get(`/api/v1/timeattendance/hrapprove?FiscalYear=${fetchYear}`)
      .then((res) => {
        if (res.data?.data) {
          setAttendanceList(res.data.data);
        } else {
          setAttendanceList([]);
        }
      })
      .catch((err) => {
        console.log(err);
        setAttendanceList([]);
      });

    // 2. ดึงรายการการลาทั้งหน่วยงาน
    const fetchLeave = axios
      .get(`/api/v1/leave/hrapprove/?Calyear=${fetchYear}`)
      .then((res) => {
        if (Array.isArray(res.data)) {
          setLeaveList(res.data);
        } else if (res.data?.data && Array.isArray(res.data.data)) {
          setLeaveList(res.data.data);
        } else {
          setLeaveList([]);
        }
      })
      .catch((err) => {
        console.log(err);
        setLeaveList([]);
      });

    Promise.all([fetchAttendance, fetchLeave]).finally(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    if (year > 0) {
      fetchAllData(year);
    }
  }, [year]);

  // =========================================================
  // ฟังก์ชันตรวจสอบเดือนปฏิทิน
  // =========================================================
  const isLeaveInMonth = (l, month) => {
    if (!month || year <= 0) return true;
    const startM = moment(l.startDate || l.start);
    const endM = moment(l.endDate || l.end || l.startDate || l.start);
    const targetGy =
      month >= 10
        ? year > 2500
          ? year - 544
          : year - 1
        : year > 2500
          ? year - 543
          : year;
    const mStart = moment(`${targetGy}-${month.toString().padStart(2, "0")}-01`, "YYYY-MM-DD").startOf("month");
    const mEnd = mStart.clone().endOf("month");

    return startM.isSameOrBefore(mEnd, "day") && endM.isSameOrAfter(mStart, "day");
  };

  const isAttendanceInMonth = (a, month) => {
    if (!month || year <= 0) return true;
    const workM = moment(a.workDate || a.checkInDT || a.checkOutDT);
    const targetGy =
      month >= 10
        ? year > 2500
          ? year - 544
          : year - 1
        : year > 2500
          ? year - 543
          : year;
    return workM.year() === targetGy && workM.month() + 1 === month;
  };

  // =========================================================
  // แปลงข้อมูลเป็น Events บนปฏิทิน
  // =========================================================
  const calendarEvents = [];

  // 1. Events การลาของบุคลากร (แท่งยาวหลายวัน)
  leaveList.forEach((l) => {
    let typeName = "ขอลา";
    let bgColor = "#43a047";

    if (l.leaveTypeKey === 1) {
      typeName = "ลาป่วย";
      bgColor = "#e53935";
    } else if (l.leaveTypeKey === 2) {
      typeName = "ลากิจ";
      bgColor = "#fb8c00";
    } else if (l.leaveTypeKey === 3) {
      typeName = "ลาพักผ่อน";
      bgColor = "#43a047";
    } else if (l.leaveTypeName) {
      typeName = l.leaveTypeName;
      bgColor = "#5c6bc0";
    }

    if (l.isCancel) {
      bgColor = "#9e9e9e";
    }

    const startDateStr = moment(l.startDate || l.start).format("YYYY-MM-DD");
    const endDateRaw = l.endDate || l.end || l.startDate || l.start;
    const endDateStr = moment(endDateRaw).add(1, "days").format("YYYY-MM-DD");

    const firstName = l.firstName || l.userView?.firstName || "";
    const lastName = l.lastName || l.userView?.lastName || "";
    const nickName = l.nickName || l.userView?.nickName || "";
    const requester = (firstName || lastName)
      ? `${firstName} ${lastName}${nickName ? ` (${nickName})` : ""}`.trim()
      : l.fullName || l.userName || "ผู้ขอลา";
    const titleText = `[${typeName}] ${requester} (${l.totalDate || 1} วัน)`;

    calendarEvents.push({
      id: `leave_${l.leaveRequestID || l.id}`,
      title: titleText,
      start: startDateStr,
      end: endDateStr,
      allDay: true,
      backgroundColor: bgColor,
      borderColor: bgColor,
      textColor: "#ffffff",
      extendedProps: {
        category: "leave",
        leaveRequestID: l.leaveRequestID || l.id,
        requesterName: requester,
        userName: l.userName,
        typeName: typeName,
        totalDate: l.totalDate,
      },
    });
  });

  // 2. Events ขอลงเวลาปฏิบัติราชการ (วันละ 1 แท่ง)
  attendanceList.forEach((a) => {
    let typeName = a.category?.categoryName || "ลงเวลา";
    let bgColor = "#0288d1";

    if (a.category?.categoryMode === 10 || a.attendanceType === 10) {
      typeName = a.category?.categoryName || "ขอตามโควต้า";
      bgColor = "#2e7d32";
    } else if (a.attendanceType === 1) {
      typeName = a.category?.categoryName || "กจ. 1 (ลืมเข้า)";
      bgColor = "#0288d1";
    } else if (a.attendanceType === 2) {
      typeName = a.category?.categoryName || "กจ. 4 (ลืมออก)";
      bgColor = "#8e24aa";
    } else if (a.attendanceType === 3) {
      typeName = a.category?.categoryName || "กจ. 1/4 (ลืมเข้า-ออก)";
      bgColor = "#d81b60";
    } else if (a.category?.categoryName) {
      typeName = a.category.categoryName;
      bgColor = "#2e7d32";
    }

    if (a.isCancel) {
      bgColor = "#9e9e9e";
    }

    const targetDate = a.workDate || a.checkInDT || a.checkOutDT;
    const dateStr = moment(targetDate).format("YYYY-MM-DD");
    const requester = a.requesterUser
      ? `${a.requesterUser.firstName} ${a.requesterUser.lastName}`
      : a.requesterName || a.userName || "ผู้ขอลงเวลา";
    const durationLabel = a.durationHours ? ` (${a.durationHours} ชม.)` : "";
    const titleText = `[${typeName}] ${requester}${durationLabel}`;

    calendarEvents.push({
      id: `att_${a.timeAttendanceRequestID}`,
      title: titleText,
      start: dateStr,
      end: dateStr,
      allDay: true,
      backgroundColor: bgColor,
      borderColor: bgColor,
      textColor: "#ffffff",
      extendedProps: {
        category: "attendance",
        timeAttendanceRequestID: a.timeAttendanceRequestID,
        requesterName: requester,
        userName: a.userName,
        typeName: typeName,
      },
    });
  });

  // จัดการคลิกบนปฏิทิน
  const handleEventClick = (info) => {
    const props = info.event.extendedProps;
    if (props.category === "attendance" && props.timeAttendanceRequestID) {
      history.push(`/hr/attendance/request/${props.timeAttendanceRequestID}`);
    } else if (props.category === "leave" && props.leaveRequestID) {
      history.push({
        pathname: `/leave/id/${props.leaveRequestID}`,
        state: { leaveRequestID: props.leaveRequestID },
      });
    }
  };

  const renderAttendanceStatus = (item) => {
    if (item.isCancel) {
      return <Chip label="ยกเลิกแล้ว" style={{ backgroundColor: "#9e9e9e", color: "#fff" }} size="small" />;
    }
    if (item.requestStatusKey === 2 || item.isApprove === true) {
      return <Chip label="อนุมัติสมบูรณ์" style={{ backgroundColor: "#4caf50", color: "#fff" }} size="small" />;
    }
    if (item.requestStatusKey === -1 || item.isApprove === false) {
      return <Chip label="ไม่อนุมัติ / ปฏิเสธ" style={{ backgroundColor: "#f44336", color: "#fff" }} size="small" />;
    }
    return <Chip label="รอดำเนินการ" style={{ backgroundColor: "#ff9800", color: "#fff" }} size="small" />;
  };

  const getAttendanceTypeText = (type) => {
    if (type === 1) return "ลงเวลาเข้า (กจ. 1)";
    if (type === 2) return "ลงเวลาออก (กจ. 4)";
    if (type === 3) return "ทั้งเข้าและออก (กจ. 1 / กจ. 4)";
    return "ขอลงเวลาตามโควต้า";
  };

  // Render Status Icon สำหรับระบบลา
  const renderLeaveApproveIcon = (statusKey) => {
    if (statusKey === 2) {
      return (
        <Tooltip title="อนุมัติ / ตรวจสอบแล้ว">
          <i className={`fa fa-check ${classes.statusIconApproved}`} aria-hidden="true" />
        </Tooltip>
      );
    }
    if (statusKey === -1) {
      return (
        <Tooltip title="ไม่อนุมัติ / ปฏิเสธ">
          <i className={`fa fa-times ${classes.statusIconRejected}`} aria-hidden="true" />
        </Tooltip>
      );
    }
    if (statusKey === 1) {
      return (
        <Tooltip title="รอการพิจารณา">
          <i className={`fa fa-question ${classes.statusIconPending}`} aria-hidden="true" />
        </Tooltip>
      );
    }
    return <span style={{ color: "#bbb" }}>-</span>;
  };

  // คัดกรองข้อมูลตามเดือนปฏิทิน, สถานะ HR/ผอ., ประเภทการลา, และคำค้นหา (Frontend Filter)
  const filteredAttendance = attendanceList
    .filter((item) => isAttendanceInMonth(item, selectedMonth))
    .filter((item) => {
      if (!searchTerm) return true;
      const s = searchTerm.toLowerCase();
      const name = (item.requesterUser ? `${item.requesterUser.firstName} ${item.requesterUser.lastName}` : item.requesterName || "").toLowerCase();
      const uname = (item.userName || "").toLowerCase();
      const reason = (item.reason || "").toLowerCase();
      return name.includes(s) || uname.includes(s) || reason.includes(s);
    });

  const filteredLeave = leaveList
    .filter((item) => isLeaveInMonth(item, selectedMonth))
    .filter((item) => {
      if (leaveTypeFilter === 0) return true;
      return item.leaveTypeKey === leaveTypeFilter;
    })
    .filter((item) => {
      if (hrApproveFilter === 0) return true;
      const hrKey = item.hrApproveStatusKey !== undefined ? item.hrApproveStatusKey : item.HRApproveStatusKey;
      return hrKey === hrApproveFilter;
    })
    .filter((item) => {
      if (drApproveFilter === 0) return true;
      const drKey = item.directorApproveStatusKey !== undefined ? item.directorApproveStatusKey : item.DirectorApproveStatusKey;
      return drKey === drApproveFilter;
    })
    .filter((item) => {
      if (!searchTerm) return true;
      const s = searchTerm.toLowerCase();
      const fn = item.firstName || item.userView?.firstName || "";
      const ln = item.lastName || item.userView?.lastName || "";
      const nn = item.nickName || item.userView?.nickName || "";
      const name = `${fn} ${ln} ${nn ? `(${nn})` : ""}`.toLowerCase();
      const uname = (item.userName || "").toLowerCase();
      const reason = (item.leaveDesc || item.title || "").toLowerCase();
      const tname = (item.leaveTypeName || "").toLowerCase();
      return name.includes(s) || uname.includes(s) || reason.includes(s) || tname.includes(s);
    });

  const totalAll = filteredAttendance.length + filteredLeave.length;
  const selectedMonthLabel = monthOptions.find((m) => m.value === selectedMonth)?.label || "";

  return (
    <div>
      {/* 1. Summary KPI Cards */}
      <GridContainer>
        <GridItem xs={12} sm={6} md={4}>
          <Card>
            <CardHeader color="primary" stats icon>
              <CardIcon color="primary">
                <SupervisorAccount />
              </CardIcon>
              <p className={classes.statsCategory}>รวมคำขอทั้งสองระบบ</p>
              <h3 className={classes.statsTitle}>
                {totalAll}
                <span className={classes.statsUnit}>รายการ</span>
              </h3>
            </CardHeader>
            <CardFooter stats>
              <div style={{ fontSize: "12px", color: "#888" }}>
                <i className="fa fa-calendar-alt mr-1 text-primary"></i>
                ประจำเดือน{selectedMonthLabel} (ปีงบประมาณ {year})
              </div>
            </CardFooter>
          </Card>
        </GridItem>

        <GridItem xs={12} sm={6} md={4}>
          <Card>
            <CardHeader color="info" stats icon>
              <CardIcon color="info">
                <AccessTime />
              </CardIcon>
              <p className={classes.statsCategory}>คำขอลงเวลาปฏิบัติราชการ</p>
              <h3 className={classes.statsTitle}>
                {filteredAttendance.length}
                <span className={classes.statsUnit}>รายการ</span>
              </h3>
            </CardHeader>
            <CardFooter stats>
              <div style={{ fontSize: "12px", color: "#888" }}>
                <i className="fa fa-users mr-1 text-info"></i>
                เดือน{selectedMonthLabel}
              </div>
            </CardFooter>
          </Card>
        </GridItem>

        <GridItem xs={12} sm={6} md={4}>
          <Card>
            <CardHeader color="warning" stats icon>
              <CardIcon color="warning">
                <FlightTakeoff />
              </CardIcon>
              <p className={classes.statsCategory}>คำขอลาทั้งหมด (Leave)</p>
              <h3 className={classes.statsTitle}>
                {filteredLeave.length}
                <span className={classes.statsUnit}>รายการ</span>
              </h3>
            </CardHeader>
            <CardFooter stats>
              <div style={{ fontSize: "12px", color: "#888" }}>
                <i className="fa fa-plane-departure mr-1 text-warning"></i>
                เดือน{selectedMonthLabel}
              </div>
            </CardFooter>
          </Card>
        </GridItem>
      </GridContainer>

      {/* 2. ปฏิทินแสดงภาพรวมทั้งหน่วยงาน (HR Calendar) */}
      <GridContainer style={{ marginTop: "10px" }}>
        <GridItem xs={12}>
          <Card className={classes.calendarCard}>
            <CardHeader color="primary">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                <div>
                  <h4 className={classes.cardTitleWhite}>
                    <Today style={{ verticalAlign: "middle", marginRight: "8px" }} />
                    ปฏิทิน HR ตรวจสอบภาพรวมการลาและการลงเวลา (ทั้งหน่วยงาน)
                  </h4>
                  <p className={classes.cardCategoryWhite}>
                    เลือกปีงบประมาณและเดือนปฏิทินเพื่อกรองข้อมูลและเลื่อนดูช่วงเวลาที่ต้องการ
                  </p>
                </div>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
                  <Button
                    color="white"
                    size="sm"
                    style={{ color: "#1976d2", fontWeight: "bold" }}
                    onClick={() => history.push("/hr/attendance/setting")}
                  >
                    <Settings style={{ marginRight: "4px", fontSize: "18px" }} />
                    ตั้งค่าประเภทการลงเวลา
                  </Button>
                  <div style={{ minWidth: "130px", color: "black", textAlign: "left" }}>
                    <TSSelect2
                      options={optionYear}
                      value={year}
                      onChange={(e) => handleYearChange(e.value)}
                    />
                  </div>
                  <div style={{ minWidth: "150px", color: "black", textAlign: "left" }}>
                    <TSSelect2
                      options={monthOptions}
                      value={selectedMonth}
                      onChange={(e) => handleMonthChange(e.value)}
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              {/* Legend คำอธิบายสี */}
              <div className={classes.legendContainer}>
                <span style={{ fontWeight: "bold", fontSize: "13px", color: "#333", marginRight: "8px" }}>
                  <i className="fa fa-tags mr-1 text-primary"></i> สัญลักษณ์สี:
                </span>
                <div className={classes.legendItem}>
                  <span className={classes.legendDot} style={{ backgroundColor: "#43a047" }}></span>
                  <span>ลาพักผ่อน (แถบยาวหลายวัน)</span>
                </div>
                <div className={classes.legendItem}>
                  <span className={classes.legendDot} style={{ backgroundColor: "#e53935" }}></span>
                  <span>ลาป่วย</span>
                </div>
                <div className={classes.legendItem}>
                  <span className={classes.legendDot} style={{ backgroundColor: "#fb8c00" }}></span>
                  <span>ลากิจ</span>
                </div>
                <div className={classes.legendItem}>
                  <span className={classes.legendDot} style={{ backgroundColor: "#0288d1" }}></span>
                  <span>กจ. 1 (ลืมเข้า)</span>
                </div>
                <div className={classes.legendItem}>
                  <span className={classes.legendDot} style={{ backgroundColor: "#8e24aa" }}></span>
                  <span>กจ. 4 (ลืมออก)</span>
                </div>
                <div className={classes.legendItem}>
                  <span className={classes.legendDot} style={{ backgroundColor: "#d81b60" }}></span>
                  <span>กจ. 1/4 (ลืมเข้า-ออก)</span>
                </div>
                <div className={classes.legendItem}>
                  <span className={classes.legendDot} style={{ backgroundColor: "#2e7d32" }}></span>
                  <span>ขอตามโควต้า</span>
                </div>
              </div>

              {/* ปฏิทิน FullCalendar (ซิงค์เดือน/ปี 2 ทางกับ Dropdown) */}
              <div style={{ minHeight: "550px" }}>
                <Fullcalendar
                  ref={calendarRef}
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                  initialView="dayGridMonth"
                  initialDate={moment().format("YYYY-MM-DD")}
                  datesSet={handleDatesSet}
                  headerToolbar={{
                    left: "prev,next today",
                    center: "title",
                    right: "dayGridMonth,listMonth",
                  }}
                  locale="th"
                  events={calendarEvents}
                  eventClick={handleEventClick}
                  height="auto"
                  displayEventTime={false}
                  buttonText={{
                    today: "วันนี้",
                    month: "รายเดือน",
                    list: "รายการ",
                  }}
                />
              </div>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {/* 3. ตารางข้อมูล HR ตรวจสอบแบบแยกแท็บ พร้อมตัวกรอง */}
      <GridContainer style={{ marginTop: "20px" }}>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="info">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                <div>
                  <h4 className={classes.cardTitleWhite}>
                    <Assignment style={{ verticalAlign: "middle", marginRight: "8px" }} />
                    ตารางข้อมูลสำหรับ HR ตรวจสอบ (ประจำเดือน{selectedMonthLabel})
                  </h4>
                  <p className={classes.cardCategoryWhite}>
                    แสดงรายการข้อมูลตามปีงบประมาณ {year} และเดือน{selectedMonthLabel}
                  </p>
                </div>
                <div style={{ minWidth: "260px" }}>
                  <TextField
                    placeholder="ค้นหาชื่อ, รหัสผู้ใช้ หรือเหตุผล..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    variant="outlined"
                    size="small"
                    style={{ backgroundColor: "#fff", borderRadius: "4px" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search style={{ color: "#777" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              </div>
            </CardHeader>
            <CardBody>
              {/* Tab Selector */}
              <Paper square style={{ marginBottom: "20px", boxShadow: "none", borderBottom: "1px solid #e0e0e0" }}>
                <Tabs
                  value={tabIndex}
                  indicatorColor="primary"
                  textColor="primary"
                  onChange={(e, val) => setTabIndex(val)}
                >
                  <Tab
                    className={classes.tabCustom}
                    label={
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Group style={{ fontSize: "18px" }} />
                        <span>ทั้งหมด ({totalAll})</span>
                      </div>
                    }
                  />
                  <Tab
                    className={classes.tabCustom}
                    label={
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <AccessTime style={{ fontSize: "18px" }} />
                        <span>ลงเวลาปฏิบัติราชการ ({filteredAttendance.length})</span>
                      </div>
                    }
                  />
                  <Tab
                    className={classes.tabCustom}
                    label={
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <FlightTakeoff style={{ fontSize: "18px" }} />
                        <span>การลา ({filteredLeave.length})</span>
                      </div>
                    }
                  />
                </Tabs>
              </Paper>

              {/* ========================================================= */}
              {/* ตารางที่ 1: รายการขอลงเวลาปฏิบัติราชการ */}
              {/* ========================================================= */}
              {(tabIndex === 0 || tabIndex === 1) && (
                <div style={{ marginBottom: tabIndex === 0 ? "35px" : "0px" }}>
                  {tabIndex === 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                      <AccessTime style={{ color: "#00acc1" }} />
                      <h4 style={{ margin: 0, fontWeight: "bold", color: "#333" }}>
                        รายการลงเวลาปฏิบัติราชการ ({filteredAttendance.length} รายการ)
                      </h4>
                    </div>
                  )}

                  <div className={classes.tableResponsive}>
                    <Table size="small">
                      <TableHead>
                        <TableRow style={{ backgroundColor: "#f9f9f9" }}>
                          <TableCell style={{ fontWeight: "bold", width: "50px" }}>ลำดับ</TableCell>
                          <TableCell style={{ fontWeight: "bold" }}>ผู้ขอลงเวลา</TableCell>
                          <TableCell style={{ fontWeight: "bold" }}>วันที่ปฏิบัติงาน</TableCell>
                          <TableCell style={{ fontWeight: "bold" }}>ประเภท / เวลา</TableCell>
                          <TableCell style={{ fontWeight: "bold" }}>เหตุผล</TableCell>
                          <TableCell style={{ fontWeight: "bold" }}>พยาน / หัวหน้า 1 ระดับ</TableCell>
                          <TableCell style={{ fontWeight: "bold" }}>สถานะ</TableCell>
                          <TableCell style={{ fontWeight: "bold", textAlign: "center" }}>จัดการ</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredAttendance.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center" style={{ color: "#888", padding: "24px" }}>
                              ไม่มีรายการขอลงเวลาที่ตรงกับเงื่อนไขในเดือน{selectedMonthLabel}
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredAttendance.map((row, idx) => {
                            const reqName = row.requesterUser
                              ? `${row.requesterUser.firstName} ${row.requesterUser.lastName}`
                              : row.requesterName || row.userName || "ผู้ขอ";
                            const witnessName = row.witnessUser
                              ? `${row.witnessUser.firstName} ${row.witnessUser.lastName}`
                              : "-";
                            const bossName = row.departmentBossUser
                              ? `${row.departmentBossUser.firstName} ${row.departmentBossUser.lastName}`
                              : (row.groupWorkBossUser
                                ? `${row.groupWorkBossUser.firstName} ${row.groupWorkBossUser.lastName}`
                                : (row.missionBossUser
                                  ? `${row.missionBossUser.firstName} ${row.missionBossUser.lastName}`
                                  : (row.bossUser ? `${row.bossUser.firstName} ${row.bossUser.lastName}` : (row.bossUserName || "-"))));

                            return (
                              <TableRow key={row.timeAttendanceRequestID || idx} hover>
                                <TableCell>{idx + 1}</TableCell>
                                <TableCell>
                                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <Avatar
                                      src={`https://e.trangskin.go.th/avatar/${row.userName}.jpg`}
                                      alt={row.userName}
                                      style={{ width: 34, height: 34 }}
                                    />
                                    <div>
                                      <span style={{ fontWeight: "bold", color: "#222" }}>{reqName}</span>
                                      <br />
                                      <small style={{ color: "#777" }}>@{row.userName}</small>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell style={{ fontWeight: "bold" }}>
                                  {moment(row.workDate || row.checkInDT || row.checkOutDT).format("D MMM BB")}
                                </TableCell>
                                <TableCell>
                                  <span style={{ color: "#007bff", fontWeight: "500" }}>
                                    {row.category?.categoryName || getAttendanceTypeText(row.attendanceType)}
                                    {row.durationHours ? ` (${row.durationHours} ชม.)` : ""}
                                  </span>
                                  <br />
                                  <small style={{ color: "#666" }}>
                                    {row.checkInDT && (row.category?.categoryMode === 10 || row.attendanceType === 10 ? `ตั้งแต่: ${moment(row.checkInDT).format("HH:mm")} ` : `เข้า: ${moment(row.checkInDT).format("HH:mm")} `)}
                                    {row.checkOutDT && (row.category?.categoryMode === 10 || row.attendanceType === 10 ? `ถึง: ${moment(row.checkOutDT).format("HH:mm")}` : `ออก: ${moment(row.checkOutDT).format("HH:mm")}`)}
                                  </small>
                                </TableCell>
                                <TableCell style={{ maxWidth: "200px", fontSize: "13px" }}>{row.reason}</TableCell>
                                <TableCell style={{ fontSize: "12.5px" }}>
                                  พยาน: <strong>{witnessName}</strong>
                                  <br />
                                  หัวหน้า: <strong>{bossName}</strong>
                                </TableCell>
                                <TableCell>{renderAttendanceStatus(row)}</TableCell>
                                <TableCell align="center">
                                  <Button
                                    color="info"
                                    size="sm"
                                    round
                                    justIcon
                                    title="ตรวจสอบ / จัดการ"
                                    onClick={() =>
                                      history.push(`/hr/attendance/request/${row.timeAttendanceRequestID}`)
                                    }
                                  >
                                    <i className="fa fa-eye"></i>
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* ========================================================= */}
              {/* ตารางที่ 2: รายการขอลา (Leave Requests) พร้อมตัวกรองประเภทการลา, HR ตรวจสอบ & ผอ.อนุมัติ */}
              {/* ========================================================= */}
              {(tabIndex === 0 || tabIndex === 2) && (
                <div>
                  {tabIndex === 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                      <FlightTakeoff style={{ color: "#e91e63" }} />
                      <h4 style={{ margin: 0, fontWeight: "bold", color: "#333" }}>
                        รายการขอลา ({filteredLeave.length} รายการ)
                      </h4>
                    </div>
                  )}

                  <div className={classes.tableResponsive}>
                    <Table size="small">
                      <TableHead>
                        <TableRow style={{ backgroundColor: "#f9f9f9" }}>
                          <TableCell style={{ fontWeight: "bold", width: "50px" }}>ลำดับ</TableCell>
                          <TableCell style={{ fontWeight: "bold" }}>ผู้ขอลา / วันที่ลา</TableCell>
                          <TableCell style={{ fontWeight: "bold", minWidth: "160px" }}>ประเภท / รายละเอียด</TableCell>
                          <TableCell style={{ fontWeight: "bold", textAlign: "center", minWidth: "90px" }}>HR ตรวจสอบ</TableCell>
                          <TableCell style={{ fontWeight: "bold", textAlign: "center", minWidth: "90px" }}>ผอ.อนุมัติ</TableCell>
                          <TableCell style={{ fontWeight: "bold", textAlign: "center" }}>จัดการ</TableCell>
                        </TableRow>
                        <TableRow style={{ backgroundColor: "#f9f9f9" }}>
                          <TableCell style={{ padding: "0px" }}></TableCell>
                          <TableCell style={{ padding: "0px" }}></TableCell>
                          <TableCell style={{ padding: "1px 2px" }}>
                            <div style={{ minWidth: "110px", color: "black", textAlign: "left", display: "inline-block" }}>
                              <TSSelect2
                                label=""
                                options={leaveTypeList}
                                value={leaveTypeFilter}
                                onChange={(e) => setLeaveTypeFilter(e.value)}
                                fontSize="0.75rem"
                                marginBottom="0px"
                                small
                              />
                            </div>
                          </TableCell>
                          <TableCell style={{ padding: "1px 2px", textAlign: "center" }}>
                            <div style={{ minWidth: "60px", color: "black", textAlign: "left", display: "inline-block" }}>
                              <TSSelect2
                                label=""
                                options={approveFilterOptions}
                                value={hrApproveFilter}
                                onChange={(e) => setHrApproveFilter(e.value)}
                                fontSize="0.75rem"
                                marginBottom="0px"
                                small
                              />
                            </div>
                          </TableCell>
                          <TableCell style={{ padding: "1px 2px", textAlign: "center" }}>
                            <div style={{ minWidth: "60px", color: "black", textAlign: "left", display: "inline-block" }}>
                              <TSSelect2
                                label=""
                                options={approveFilterOptions}
                                value={drApproveFilter}
                                onChange={(e) => setDrApproveFilter(e.value)}
                                fontSize="0.75rem"
                                marginBottom="0px"
                                small
                              />
                            </div>
                          </TableCell>
                          <TableCell style={{ padding: "0px" }}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredLeave.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center" style={{ color: "#888", padding: "24px" }}>
                              ไม่มีรายการขอลาที่ตรงกับเงื่อนไขในเดือน{selectedMonthLabel}
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredLeave.map((row, idx) => {
                            const firstName = row.firstName || row.userView?.firstName || "";
                            const lastName = row.lastName || row.userView?.lastName || "";
                            const nickName = row.nickName || row.userView?.nickName || "";

                            let reqName = "";
                            if (firstName || lastName) {
                              reqName = `${firstName} ${lastName}`.trim();
                              if (nickName) {
                                reqName += ` (${nickName})`;
                              }
                            } else {
                              reqName = row.fullName || row.userName || "ผู้ขอลา";
                            }

                            const leaveId = row.leaveRequestID || row.id;
/*
                            let statusChip = <Chip label="รออนุมัติ" style={{ backgroundColor: "#ff9800", color: "#fff" }} size="small" />;
                            if (row.isCancel) {
                              statusChip = <Chip label="ยกเลิก" style={{ backgroundColor: "#9e9e9e", color: "#fff" }} size="small" />;
                            } else if (row.isApprove === true || row.isApprove === 1) {
                              statusChip = <Chip label="อนุมัติแล้ว" style={{ backgroundColor: "#4caf50", color: "#fff" }} size="small" />;
                            } else if (row.isApprove === false || row.isApprove === -1) {
                              statusChip = <Chip label="ไม่อนุมัติ" style={{ backgroundColor: "#f44336", color: "#fff" }} size="small" />;
                            }
*/
                            const hrStatus = row.hrApproveStatusKey !== undefined ? row.hrApproveStatusKey : row.HRApproveStatusKey;
                            const drStatus = row.directorApproveStatusKey !== undefined ? row.directorApproveStatusKey : row.DirectorApproveStatusKey;

                            return (
                              <TableRow
                                key={leaveId || idx}
                                hover
                                style={row.isCancel ? { textDecoration: "line-through", opacity: 0.7 } : {}}
                              >
                                <TableCell>{idx + 1}</TableCell>
                                <TableCell>
                                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <Avatar
                                      src={`https://e.trangskin.go.th/avatar/${row.userName}.jpg`}
                                      alt={row.userName}
                                      style={{ width: 36, height: 36 }}
                                    />
                                    <div>
                                      <span style={{ fontWeight: "bold", color: "#222" }}>{reqName}</span>
                                      <div style={{ color: "#555", fontSize: "12px", marginTop: "2px" }}>
                                        <i className="fa fa-calendar-alt mr-1" style={{ color: "#1976d2", marginRight: "4px" }}></i>
                                        {moment(row.startDate || row.start).format("D MMM BB")}
                                        {(row.endDate || row.end) &&
                                          moment(row.startDate || row.start).format("YYYY-MM-DD") !==
                                            moment(row.endDate || row.end).format("YYYY-MM-DD") &&
                                          ` - ${moment(row.endDate || row.end).format("D MMM BB")}`}
                                      </div>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell style={{ maxWidth: "220px", fontSize: "13px" }}>
                                  <span style={{ color: "#e91e63", fontWeight: "bold" }}>
                                    {row.leaveTypeName || (row.leaveTypeKey === 1 ? "ลาป่วย" : row.leaveTypeKey === 2 ? "ลากิจ" : row.leaveTypeKey === 3 ? "ลาพักผ่อน" : "การลา")}
                                  </span> {row.totalDate || 1} วัน
                                  <br />
                                  <small style={{ color: "#666" }}>{row.leaveDesc || row.title || "-"}</small>
                                </TableCell>
                                <TableCell align="center">
                                  {renderLeaveApproveIcon(hrStatus)}
                                </TableCell>
                                <TableCell align="center">
                                  {renderLeaveApproveIcon(drStatus)}
                                </TableCell>
                                <TableCell align="center">
                                  <Button
                                    color="info"
                                    size="sm"
                                    round
                                    justIcon
                                    title="ตรวจสอบ / จัดการ"
                                    onClick={() =>
                                      history.push({
                                        pathname: `/leave/id/${leaveId}`,
                                        state: { leaveRequestID: leaveId },
                                      })
                                    }
                                  >
                                    <i className="fa fa-eye"></i>
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
};

export default HRAttendance;
